/* eslint-disable no-restricted-syntax */
import React from 'react';

import { namedRoutes } from '@confluence/named-routes/entry-points/named-routes.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import { RedirectLegacyCopyPageLoader } from '@confluence/redirect-legacy-copy-page';
import { RedirectLegacyViewPageLoader } from '@confluence/redirect-legacy-view-page';
import { processNavigationParams } from '@confluence/ssr-app/entry-points/routes.ts';

import { RedirectOldCreatePage } from './routes/RedirectOldCreatePage.jsx';
import { AdminForgeAppsRoute } from './routes/AdminForgeAppsRoute.tsx';
import { AdminForgeAppsRedirectRoute } from './routes/AdminForgeAppsRedirect.tsx';
import { AdvancedSearchRoute } from './routes/AdvancedSearchRoute.tsx';
import { BetaFeaturesRoute } from './routes/BetaFeaturesRoute.tsx';
import { ExternalShareRoute } from './routes/ExternalShareRoute.tsx';
import { GlobalPageForgeAppsRoute } from './routes/GlobalPageForgeAppsRoute.tsx';
import { GlobalPermissionRoute } from './routes/GlobalPermissionRoute.tsx';
import { AdminCompanyHubPermissionsRoute } from './routes/AdminCompanyHubPermissionsRoute.tsx';
import {
	CompanyHomeBuilderRoute,
	CustomizeNavigationRoute,
} from './routes/CustomizeNavigationRoute.tsx';
import { GlobalAutomationRoute } from './routes/GlobalAutomationRoute.tsx';
import { GlobalPermissionUserDetailsRoute } from './routes/GlobalPermissionUserDetailsRoute.tsx';
import { PdfExportPageRoute } from './routes/PdfExportPageRoute.tsx';
import { PdfExportSpaceRoute } from './routes/PdfExportSpaceRoute.tsx';
import { PeopleDirectoryRoute } from './routes/PeopleDirectoryRoute.tsx';
import { MarketplaceRoute } from './routes/MarketplaceRoute.tsx';
import { SpaceForgeAppsRoute } from './routes/SpaceForgeAppsRoute.tsx';
import { SpaceSettingsForgeAppsRoute } from './routes/SpaceSettingsForgeAppsRoute.tsx';
import { SpaceSettingsArchiveSpaceRoute } from './routes/SpaceSettingsArchiveSpaceRoute.tsx';
import { SpaceSettingsEditorConversionRoute } from './routes/SpaceSettingsEditorConversionRoute.tsx';
import { SpaceSettingsContentStateRoute } from './routes/SpaceSettingsContentStateRoute.tsx';
import { SpaceSettingsRelatedPagesRoute } from './routes/SpaceSettingsRelatedPagesRoute.tsx';
import { SpaceSettingsEditFeaturesRoute } from './routes/SpaceSettingsEditFeaturesRoute.tsx';
import { SpaceSettingsDetailsRoute } from './routes/SpaceSettingsDetailsRoute.tsx';
import { SpaceSettingsAgentContextRoute } from './routes/SpaceSettingsAgentContextRoute.tsx';
import { SpaceLookAndFeelSettingsRoute } from './routes/SpaceLookAndFeelSettingsRoute.tsx';
import { SpaceArchivedPagesRoute } from './routes/SpaceArchivedPagesRoute.tsx';
import { SpaceContributorsRoute } from './routes/SpaceContributorsRoute.tsx';
import { SpaceSuggestionsRoute } from './routes/SpaceSuggestionsRoute.tsx';
import { SpaceSettingsDataClassificationRoute } from './routes/SpaceSettingsDataClassificationRoute.tsx';
import { SpaceSettingsWatermarkConfigurationRoute } from './routes/SpaceSettingsWatermarkConfigurationRoute.tsx';
import { MyCalendarRoute } from './routes/MyCalendarRoute.tsx';
import { MultipleCalendarRoute } from './routes/MultipleCalendarRoute.tsx';
import { EmbeddedMultipleCalendarRoute } from './routes/EmbeddedMultipleCalendarRoute.tsx';
import { SpaceTeamCalendarsRoute } from './routes/SpaceTeamCalendarsRoute.tsx';
import { SpaceEmbeddedSubcalendarRoute } from './routes/SpaceEmbeddedSubcalendarRoute.tsx';
import { SpaceDirectoryRoute } from './routes/SpaceDirectoryRoute.tsx';
import { SpaceSettingsLandingPageRoute } from './routes/SpaceSettingsLandingPageRoute.tsx';
import { SpaceOverviewRoute } from './routes/SpaceOverviewRoute.tsx';
import { CompanyHubRoute } from './routes/CompanyHubRoute.tsx';
import { CompanyHubAnalyticsRoute } from './routes/CompanyHubAnalyticsRoute.tsx';
import { CompanyHubEditRoute } from './routes/CompanyHubEditRoute.tsx';
import { CompanyHubEditorRoute } from './routes/CompanyHubEditorRoute.tsx';
import { CompanyHubSettingsRoute } from './routes/CompanyHubSettingsRoute.tsx';
import { SpacePagesRoute } from './routes/SpacePagesRoute.tsx';
import { BlogHomeRoute } from './routes/BlogHomeRoute.tsx';
import { SpaceBulkToolsRoute } from './routes/SpaceBulkToolsRoute.tsx';
import { TemplateMigrationPreviewRoute } from './routes/TemplateMigrationPreviewRoute.tsx';
import { TemplateEditorRoute } from './routes/TemplateEditorRoute.tsx';
import { OnboardingRoute } from './routes/OnboardingRoute.tsx';
import { ContentHistoryRoute } from './routes/ContentHistoryRoute.tsx';
import { TemplateGalleryRoute } from './routes/TemplateGalleryRoute.tsx';
import { MobileMacroRendererRoute } from './routes/MobileMacroRendererRoute.tsx';
import { MobileTakeoverPageRoute } from './routes/MobileTakeoverPageRoute.tsx';
import { WikiRoute } from './routes/WikiRoute.tsx';
import { HomeRoute } from './routes/HomeRoute.tsx';
import { CreateBlogRoute } from './routes/CreateBlogRoute.tsx';
import { CreatePageRoute } from './routes/CreatePageRoute.tsx';
import { EditPageRoute } from './routes/EditPageRoute.tsx';
import { EditBlogRoute } from './routes/EditBlogRoute.tsx';
import { EditPageOrBlogV2Route } from './routes/EditPageOrBlogV2Route.tsx';
import { EditPageEmbedRoute } from './routes/EditPageEmbedRoute.tsx';
import { EditPageSmartCreateRoute } from './routes/EditPageSmartCreateRoute.tsx';
import { AIPlannerConfluenceBridgeRoute } from './routes/AIPlannerConfluenceBridgeRoute.js';
import { SpaceSettingsAutomationRoute } from './routes/SpaceSettingsAutomationRoute.tsx';
import { SpaceCustomContentListRoute } from './routes/SpaceCustomContentListRoute.tsx';
import { CustomContentOnPageRoute } from './routes/CustomContentOnPageRoute.tsx';
import { CustomContentViewRoute } from './routes/CustomContentViewRoute.tsx';
import { PublicLinksSettingsPageRoute } from './routes/PublicLinksSettingsPageRoute.tsx';
import { AnonymousAccessSettingsPageRoute } from './routes/AnonymousAccessSettingsPageRoute.tsx';
import { SpaceManagementPageRoute } from './routes/SpaceManagementPageRoute.tsx';
import { SpaceDefaultsPageRoute } from './routes/SpaceDefaultsPageRoute.tsx';
import { SpaceRolesPageRoute } from './routes/SpaceRolesPageRoute.tsx';
import { SitePermissionsPageRoute } from './routes/SitePermissionsPageRoute.tsx';
import { GuestsSettingsPageRoute } from './routes/GuestsSettingsPageRoute.tsx';
import { AppsPermissionsSettingsPageRoute } from './routes/AppsPermissionsSettingsPageRoute.tsx';
import { JsmAccessSettingsPageRoute } from './routes/JsmAccessSettingsPageRoute.tsx';
import { ContactAdministratorRoute } from './routes/ContactAdministratorRoute.tsx';
import { SpacePermissionsSettingsRoute } from './routes/SpacePermissionsSettingsRoute.tsx';
import { RoleBasedAccessControlHubRoute } from './routes/RoleBasedAccessControlHubRoute.tsx';
import { AdminAnnouncementBannerRendererRoute } from './routes/AdminAnnouncementBannerRendererRoute.tsx';
import { AdminCenterRoute } from './routes/AdminCenterRoute.tsx';
import { AdminCenterSpaceRoute } from './routes/AdminCenterSpaceRoute.tsx';
import { AdminSettingsRoute } from './routes/AdminSettingsRoute.tsx';
import { SpaceSettingsExportSpaceUnavailableRoute } from './routes/SpaceSettingsExportSpaceUnavailableRoute.tsx';
import { BulkSpacePermissionsRoute } from './routes/BulkSpacePermissionsRoute.tsx';
import { AdminTeamCalendarsRoute } from './routes/AdminTeamCalendarsRoute.tsx';
import { AdminTeamControlsRoute } from './routes/AdminTeamControlsRoute.tsx';
import { AdminConfigurationRoute } from './routes/AdminConfigurationRoute.tsx';
import { AdminDefaultSpaceLogoConfigRoute } from './routes/AdminDefaultSpaceLogoConfigRoute.tsx';
import { AdminHeaderAndFooterRoute } from './routes/AdminHeaderAndFooterRoute.tsx';
import { AdminTemplatesAndBlueprintsRoute } from './routes/AdminTemplatesAndBlueprintsRoute.tsx';
import { AdminNotificationsRoute } from './routes/AdminNotificationsRoute.tsx';
import { PerformanceInsightsRoute } from './routes/AdminPerformanceRoute.tsx';
import { AdminSpaceReportsRoute } from './routes/AdminSpaceReportsRoute.tsx';
import { AdminCodeMacroConfigurationRoute } from './routes/AdminCodeMacroConfigurationRoute.tsx';
import { AdminColorSchemeRoute } from './routes/AdminColorSchemeRoute.tsx';
import { AdminSecurityConfigurationRoute } from './routes/AdminSecurityConfigurationRoute.tsx';
import { AdminShareableLinksRoute } from './routes/AdminShareableLinksRoute.tsx';
import { AdminShareableLinksConfigurationRoute } from './routes/AdminShareableLinksConfigurationRoute.tsx';
import { AdminApplicationNavigatorRoute } from './routes/AdminApplicationNavigatorRoute.tsx';
import { AdminMacroUsageRoute } from './routes/AdminMacroUsageRoute.tsx';
import { AdminTrashedSpacesRoute } from './routes/AdminTrashedSpacesRoute.tsx';
import { AdminImportSpacesRoute } from './routes/AdminImportSpacesRoute.tsx';
import { AdminJiraMacroRepairRoute } from './routes/AdminJiraMacroRepairRoute.tsx';
import { AdminThemesRoute } from './routes/AdminThemesRoute.tsx';
import { AdminPdfExportRoute } from './routes/AdminPdfExportRoute.tsx';
import { AdminWatermarkConfigurationRoute } from './routes/AdminWatermarkConfigurationRoute.tsx';
import { SpaceManagerRoute } from './routes/SpaceManagerRoute.tsx';
import { SpaceMembersRoute } from './routes/SpaceMembersRoute.tsx';
import { AdminSiteSpacePermissionsRoute } from './routes/AdminSiteSpacePermissionsRoute.tsx';
import { RedirectToSpaceOverview } from './routes/RedirectToSpaceOverview.tsx';
import { RedirectToHomeWithQuery } from './routes/RedirectToHomeWithQuery.tsx';
import { ActionRedirectLegacyRoute } from './routes/ActionRedirectLegacyRoute.tsx';
import { ResumeDraftLegacyRoute } from './routes/ResumeDraftLegacyRoute.tsx';
import { WhiteboardRoute } from './routes/WhiteboardRoute.tsx';
import { SlidesRoute } from './routes/SlidesRoute.tsx';
import { FolderViewRoute } from './routes/FolderViewRoute.tsx';
import { EmbedViewRoute } from './routes/EmbedViewRoute.tsx';
import { DatabaseViewRoute } from './routes/DatabaseViewRoute.tsx';
import { SpaceIndexRoute } from './routes/SpaceIndexRoute.tsx';
import { CreateContentRoute } from './routes/CreateContentRoute.tsx';
import { PageSplitRoute } from './routes/PageSplitRoute.tsx';
import { ViewPageOrBlogRoute } from './routes/ViewPageOrBlogRoute.tsx';
import { TopicsRoute } from './routes/TopicsRoute.tsx';
import { AskQuestionRoute, QuestionsRoute } from './routes/QuestionsRoute.tsx';
import { QuestionRoute } from './routes/QuestionRoute.tsx';
import { SiteAnalyticsRoute } from './routes/SiteAnalyticsRoute.tsx';
import { SiteAnalyticsPermissionsRoute } from './routes/SiteAnalyticsPermissionsRoute.tsx';
import { PageAnalyticsRoute } from './routes/PageAnalyticsRoute.tsx';
import { SpaceAnalyticsRoute } from './routes/SpaceAnalyticsRoute.tsx';
import { SpaceAnalyticsPermissionsRoute } from './routes/SpaceAnalyticsPermissionsRoute.tsx';
import { LazySpaceLegacyContentRoute } from './routes/SpaceLegacyContentRoute.tsx';
import { LazySpaceAvatarsSettingsRoute } from './routes/SpaceAvatarsSettingsRoute.tsx';
import { LazySpaceStatusAndApprovalsRoute } from './routes/SpaceStatusAndApprovalsRoute.tsx';
import { SpaceSmartCreateRoute } from './routes/SpaceSmartCreateRoute.tsx';
import { UserAccessRoute } from './routes/UserAccessRoute.tsx';
import { LazyExportSpaceRoute } from './routes/ExportSpaceRoute.tsx';
import { GlobalQuestionsRoute } from './routes/GlobalQuestionsRoute.tsx';
import { GlobalQuestionRoute } from './routes/GlobalQuestionRoute.tsx';
import { ImporterDashboardRoute } from './routes/ImporterDashboardRoute.tsx';
import { AdminNBMMigrationRoute } from './routes/AdminNBMMigrationRoute.tsx';
import { AdminDCScriptMigrationRoute } from './routes/AdminDCScriptMigrationRoute.tsx';
import { AdminNBMPerformanceReportRoute } from './routes/AdminNBMPerformanceReportRoute.tsx';
import { AttachmentsPageRoute } from './routes/AttachmentsPageRoute.tsx';
import { AISearchRoute } from './routes/AISearchRoute.tsx';
import { PermissionsExportRoute } from './routes/PermissionsExportRoute.tsx';
import { PermissionsCombinationsRoute } from './routes/PermissionsCombinationsRoute.tsx';
import { SystemOperationsRoute } from './routes/SystemOperationsRoute.tsx';
import { AuditLogRoute } from './routes/AuditLogRoute.tsx';
import { AccessLinkRoute } from './routes/AccessLinkRoute.tsx';
import { ShareableLinkSpaceSettingsRoute } from './routes/ShareableLinkSpaceSettingsRoute.tsx';
import { ShareableLinkSpaceSettingsConfigurationRoute } from './routes/ShareableLinkSpaceSettingsConfigurationRoute.tsx';

function redirect(name) {
	const redirectComponent = ({ params = {} }) => <Redirection name={name} params={params} />;
	redirectComponent.NAVIGATION_PARAMS = () => false;
	return redirectComponent;
}

/**
 * Please read patterns/routing.md
 */
export const routeComponents = processNavigationParams({
	[namedRoutes.BASE.name]: redirect(namedRoutes.WIKI.name),
	[namedRoutes.WIKI.name]: WikiRoute,
	[namedRoutes.HOME.name]: HomeRoute,
	[namedRoutes.MOBILE_TAKEOVER_PAGE.name]: MobileTakeoverPageRoute,
	[namedRoutes.MOBILE_MACRO_RENDERER.name]: MobileMacroRendererRoute,
	[namedRoutes.TEMPLATE_GALLERY.name]: TemplateGalleryRoute,
	[namedRoutes.EDIT_PAGE.name]: EditPageRoute,
	[namedRoutes.EDIT_BLOG.name]: EditBlogRoute,
	[namedRoutes.EDIT_PAGE_V2.name]: EditPageOrBlogV2Route,
	[namedRoutes.EDIT_BLOG_V2.name]: EditPageOrBlogV2Route,
	[namedRoutes.EDIT_PAGE_EMBED.name]: EditPageEmbedRoute,
	[namedRoutes.EDIT_PAGE_SMART_CREATE.name]: EditPageSmartCreateRoute,
	[namedRoutes.AI_PLANNER_CONFLUENCE_BRIDGE.name]: AIPlannerConfluenceBridgeRoute,
	[namedRoutes.CREATE_CONTENT.name]: CreateContentRoute,
	[namedRoutes.CREATE_PAGE.name]: CreatePageRoute,
	[namedRoutes.CREATE_BLOG.name]: CreateBlogRoute,
	[namedRoutes.CREATE_PAGE_ACTION_LEGACY.name]: RedirectOldCreatePage,
	[namedRoutes.CREATE_BLOG_ACTION_LEGACY.name]: RedirectOldCreatePage,
	[namedRoutes.CREATE_PAGE_VARIABLES_ACTION_LEGACY.name]: RedirectOldCreatePage,
	[namedRoutes.RESUME_DRAFT_LEGACY.name]: ResumeDraftLegacyRoute,
	[namedRoutes.COPY_PAGE_LEGACY.name]: RedirectLegacyCopyPageLoader,
	[namedRoutes.DASHBOARD_ACTION_LEGACY.name]: redirect(namedRoutes.HOME.name),
	[namedRoutes.SPA_ACTION_LEGACY.name]: redirect(namedRoutes.HOME.name),
	[namedRoutes.INDEX_ACTION_LEGACY.name]: redirect(namedRoutes.HOME.name),
	[namedRoutes.VIEW_PAGE_ACTION_LEGACY.name]: RedirectLegacyViewPageLoader,
	[namedRoutes.SPACE_OVERVIEW_INDEX_ACTION_LEGACY.name]: RedirectToSpaceOverview,
	[namedRoutes.SPACE_INDEX_ACTION_LEGACY.name]: RedirectToSpaceOverview,
	// it should be after "INDEX_ACTION_LEGACY", and can't be the last one, as it will brake navigation to space settings
	[namedRoutes.ACTION_REDIRECT_LEGACY.name]: ActionRedirectLegacyRoute,
	[namedRoutes.SPACE_DIRECTORY.name]: SpaceDirectoryRoute,
	[namedRoutes.SPACE_OVERVIEW_REDIRECT_LEGACY.name]: RedirectToSpaceOverview,
	[namedRoutes.SPACE_INDEX.name]: SpaceIndexRoute,
	[namedRoutes.SPACE_OVERVIEW.name]: SpaceOverviewRoute,
	[namedRoutes.HUB.name]: CompanyHubRoute,
	[namedRoutes.HUB_EDIT.name]: CompanyHubEditorRoute,
	[namedRoutes.COMPANY_HUB.name]: CompanyHubRoute,
	[namedRoutes.COMPANY_HUB_ANALYTICS.name]: CompanyHubAnalyticsRoute,
	[namedRoutes.COMPANY_HUB_EDIT.name]: CompanyHubEditRoute,
	[namedRoutes.COMPANY_HUB_SETTINGS.name]: CompanyHubSettingsRoute,
	[namedRoutes.GLOBAL_PAGE_FORGE.name]: GlobalPageForgeAppsRoute,
	[namedRoutes.SPACE_PAGES.name]: SpacePagesRoute,
	[namedRoutes.VIEW_PAGE.name]: ViewPageOrBlogRoute,
	[namedRoutes.PAGE_SPLIT.name]: PageSplitRoute,
	[namedRoutes.BLOG_HOME.name]: BlogHomeRoute,
	[namedRoutes.VIEW_BLOG_DATE_LEGACY.name]: ViewPageOrBlogRoute,
	[namedRoutes.VIEW_BLOG.name]: ViewPageOrBlogRoute,
	[namedRoutes.WHITEBOARD_HOME.name]: redirect(namedRoutes.SPACE_PAGES.name),
	[namedRoutes.WHITEBOARD_BOARD.name]: WhiteboardRoute,
	[namedRoutes.SLIDES_VIEW.name]: SlidesRoute,
	[namedRoutes.DATABASE_VIEW.name]: DatabaseViewRoute,
	[namedRoutes.EMBED_VIEW.name]: EmbedViewRoute,
	[namedRoutes.FOLDER_VIEW.name]: FolderViewRoute,
	[namedRoutes.CONTACT_ADMINISTRATOR.name]: ContactAdministratorRoute,
	[namedRoutes.SPACE_CALENDARS.name]: SpaceTeamCalendarsRoute,
	[namedRoutes.SPACE_CALENDARS_SUBCALENDAR.name]: SpaceTeamCalendarsRoute,
	[namedRoutes.SPACE_EMBEDDED_SUBCALENDAR.name]: SpaceEmbeddedSubcalendarRoute,
	[namedRoutes.MULTIPLE_SUBCALENDAR.name]: MultipleCalendarRoute,
	[namedRoutes.EMBEDDED_MULTIPLE_SUBCALENDAR.name]: EmbeddedMultipleCalendarRoute,
	[namedRoutes.DISCOVER.name]: RedirectToHomeWithQuery({ feed: '' }),
	[namedRoutes.ALL_UPDATES.name]: RedirectToHomeWithQuery({ feed: 0 }),
	[namedRoutes.POPULAR.name]: RedirectToHomeWithQuery({ feed: 1, feedType: 'POPULAR' }),
	[namedRoutes.MY_WORK_HOME.name]: RedirectToHomeWithQuery({ recentTab: 1 }),
	[namedRoutes.MY_WORK_DRAFTS.name]: RedirectToHomeWithQuery({ recentTab: 2 }),
	[namedRoutes.RECENT_DRAFTS.name]: RedirectToHomeWithQuery({ recentTab: 2 }),
	[namedRoutes.MY_WORK_RECENT.name]: RedirectToHomeWithQuery({ recentTab: 1, filter: 'WORKED_ON' }),
	[namedRoutes.RECENT_PAGES.name]: RedirectToHomeWithQuery({ recentTab: 1 }),
	[namedRoutes.MY_RECENTLY_VISITED.name]: RedirectToHomeWithQuery({ recentTab: 0 }),
	[namedRoutes.RECENT_VISITED.name]: RedirectToHomeWithQuery({ recentTab: 0 }),
	[namedRoutes.MY_SAVED_FOR_LATER.name]: RedirectToHomeWithQuery({ recentTab: 3 }),
	[namedRoutes.RECENT_SAVED.name]: RedirectToHomeWithQuery({ recentTab: 3 }),
	[namedRoutes.TEMPLATE_MIGRATION_PREVIEW.name]: TemplateMigrationPreviewRoute,
	[namedRoutes.EDIT_TEMPLATE.name]: TemplateEditorRoute,
	[namedRoutes.EDIT_TEMPLATE_BLUEPRINT.name]: TemplateEditorRoute,
	[namedRoutes.EDIT_TEMPLATE_SPACE.name]: TemplateEditorRoute,
	[namedRoutes.CREATE_TEMPLATE.name]: TemplateEditorRoute,
	[namedRoutes.CREATE_TEMPLATE_SPACE.name]: TemplateEditorRoute,
	[namedRoutes.ADVANCED_SEARCH.name]: AdvancedSearchRoute,
	[namedRoutes.EXTERNAL_SHARE.name]: ExternalShareRoute,
	[namedRoutes.ACCESS_LINK.name]: AccessLinkRoute,
	[namedRoutes.PEOPLE_DIRECTORY.name]: PeopleDirectoryRoute,
	[namedRoutes.MARKETPLACE_APP_REQUESTS.name]: MarketplaceRoute,
	[namedRoutes.MARKETPLACE_MANAGE_PROMOTIONS.name]: MarketplaceRoute,
	[namedRoutes.MARKETPLACE_FIND_APPS.name]: MarketplaceRoute,
	[namedRoutes.CONTENT_HISTORY.name]: ContentHistoryRoute,
	[namedRoutes.ONBOARDING.name]: OnboardingRoute,
	[namedRoutes.ADMIN_GLOBAL_AUTOMATION.name]: GlobalAutomationRoute,
	[namedRoutes.ADMIN_NEW_GLOBAL_PERMISSIONS.name]: GlobalPermissionRoute,
	[namedRoutes.ADMIN_COMPANY_HUB_PERMISSIONS.name]: AdminCompanyHubPermissionsRoute,
	[namedRoutes.ADMIN_COMPANY_HOME_BUILDER.name]: CompanyHomeBuilderRoute,
	[namedRoutes.ADMIN_CUSTOMIZE_NAVIGATION.name]: CustomizeNavigationRoute,
	[namedRoutes.ADMIN_NEW_GLOBAL_PERMISSIONS_USER_DETAILS.name]: GlobalPermissionUserDetailsRoute,
	[namedRoutes.ADMIN_EDIT_EXTERNAL_SHARING.name]: PublicLinksSettingsPageRoute,
	[namedRoutes.ADMIN_ANONYMOUS_ACCESS.name]: AnonymousAccessSettingsPageRoute,
	[namedRoutes.ADMIN_SPACE_MANAGEMENT.name]: SpaceManagementPageRoute,
	[namedRoutes.ADMIN_SPACE_DEFAULTS.name]: SpaceDefaultsPageRoute,
	[namedRoutes.ADMIN_GUESTS.name]: GuestsSettingsPageRoute,
	[namedRoutes.ADMIN_APP_PERMISSIONS.name]: AppsPermissionsSettingsPageRoute,
	[namedRoutes.ADMIN_JSM_ACCESS.name]: JsmAccessSettingsPageRoute,
	[namedRoutes.ADMIN_SPACE_ROLES.name]: SpaceRolesPageRoute,
	[namedRoutes.ADMIN_SITE_PERMISSIONS.name]: SitePermissionsPageRoute,
	[namedRoutes.ADMIN_FORGE_APPS_REDIRECT.name]: AdminForgeAppsRedirectRoute,
	[namedRoutes.ADMIN_FORGE_APPS.name]: AdminForgeAppsRoute,
	[namedRoutes.ADMIN_BETA_FEATURES.name]: redirect(namedRoutes.ADMIN_NEW_FEATURES.name),
	[namedRoutes.ADMIN_NEW_FEATURES.name]: BetaFeaturesRoute,
	[namedRoutes.ADMIN_NOTIFICATIONS.name]: AdminNotificationsRoute,
	[namedRoutes.PERFORMANCE_INSIGHTS.name]: PerformanceInsightsRoute,
	[namedRoutes.SITE_ANALYTICS.name]: SiteAnalyticsRoute,
	[namedRoutes.SITE_ANALYTICS_PERMISSIONS.name]: SiteAnalyticsPermissionsRoute,
	[namedRoutes.SPACE_ANALYTICS.name]: SpaceAnalyticsRoute,
	[namedRoutes.SPACE_ANALYTICS_PERMISSIONS.name]: SpaceAnalyticsPermissionsRoute,
	[namedRoutes.SPACE_SMART_CREATE.name]: SpaceSmartCreateRoute,
	[namedRoutes.PAGE_ANALYTICS.name]: PageAnalyticsRoute,
	[namedRoutes.SPACE_ARCHIVED_PAGES.name]: SpaceArchivedPagesRoute,
	[namedRoutes.SPACE_CONTRIBUTORS.name]: SpaceContributorsRoute,
	[namedRoutes.SPACE_SUGGESTIONS.name]: SpaceSuggestionsRoute,
	[namedRoutes.SPACE_BULK_TOOLS.name]: SpaceBulkToolsRoute,
	[namedRoutes.SPACE_SETTINGS_LANDING_PAGE.name]: SpaceSettingsLandingPageRoute,
	[namedRoutes.SPACE_SETTINGS_LANDING_PAGE_ALT.name]: redirect(
		namedRoutes.SPACE_SETTINGS_LANDING_PAGE.name,
	),
	[namedRoutes.SPACE_FORGE_APPS.name]: SpaceForgeAppsRoute,
	[namedRoutes.SPACE_CUSTOM_CONTENT_LIST.name]: SpaceCustomContentListRoute,
	[namedRoutes.SPACE_SETTINGS_FORGE_APPS.name]: SpaceSettingsForgeAppsRoute,
	[namedRoutes.SPACE_SETTINGS_ARCHIVE_SPACE.name]: SpaceSettingsArchiveSpaceRoute,
	[namedRoutes.SPACE_SETTINGS_EDITOR_CONVERSION.name]: SpaceSettingsEditorConversionRoute,
	[namedRoutes.SPACE_SETTINGS_AUTOMATION.name]: SpaceSettingsAutomationRoute,
	[namedRoutes.SPACE_SETTINGS_AVATARS_AND_STATUS.name]: SpaceSettingsContentStateRoute,
	[namedRoutes.SPACE_SETTINGS_CONTENT_STATE.name]: SpaceSettingsContentStateRoute,
	[namedRoutes.SPACE_SETTINGS_DATA_CLASSIFICATION.name]: SpaceSettingsDataClassificationRoute,
	[namedRoutes.SPACE_SETTINGS_WATERMARK_CONFIGURATION.name]:
		SpaceSettingsWatermarkConfigurationRoute,
	[namedRoutes.SPACE_SETTINGS_EXPORT_SPACE_UNAVAILABLE.name]:
		SpaceSettingsExportSpaceUnavailableRoute,
	[namedRoutes.SPACE_SETTINGS_EXPORT.name]: LazyExportSpaceRoute,
	[namedRoutes.SPACE_LEGACY_CONTENT.name]: LazySpaceLegacyContentRoute,
	[namedRoutes.SPACE_SETTINGS_AVATARS.name]: LazySpaceAvatarsSettingsRoute,
	[namedRoutes.SPACE_SETTINGS_STATUS_AND_APPROVALS.name]: LazySpaceStatusAndApprovalsRoute,
	[namedRoutes.SPACE_SETTINGS_RELATED_PAGES.name]: SpaceSettingsRelatedPagesRoute,
	[namedRoutes.SPACE_SETTINGS_EDIT_FEATURES.name]: SpaceSettingsEditFeaturesRoute,
	[namedRoutes.SPACE_SETTINGS_AGENT_CONTEXT.name]: SpaceSettingsAgentContextRoute,
	[namedRoutes.SPACE_SETTINGS_DETAILS.name]: SpaceSettingsDetailsRoute,
	[namedRoutes.SPACE_QUESTIONS_NATIVE.name]: QuestionsRoute,
	[namedRoutes.SPACE_QUESTIONS_ASK_NATIVE.name]: AskQuestionRoute,
	[namedRoutes.SPACE_QUESTION_NATIVE.name]: QuestionRoute,
	[namedRoutes.QUESTIONS_NATIVE.name]: GlobalQuestionsRoute,
	[namedRoutes.QUESTION_NATIVE.name]: GlobalQuestionRoute,
	[namedRoutes.QUESTIONS_TOPICS_NATIVE.name]: GlobalQuestionRoute,
	[namedRoutes.QUESTIONS_TOPIC_NATIVE.name]: GlobalQuestionRoute,
	[namedRoutes.QUESTIONS_EXPERTS_NATIVE.name]: GlobalQuestionRoute,
	[namedRoutes.MY_CALENDAR.name]: MyCalendarRoute,
	[namedRoutes.PDF_EXPORT_PAGE.name]: PdfExportPageRoute,
	[namedRoutes.PDF_EXPORT_SPACE.name]: PdfExportSpaceRoute,
	[namedRoutes.CUSTOM_CONTENTS_ON_PAGE_LIST.name]: CustomContentOnPageRoute,
	[namedRoutes.CUSTOM_CONTENT_VIEW.name]: CustomContentViewRoute,
	[namedRoutes.SPACE_PERMISSIONS_SETTINGS.name]: SpacePermissionsSettingsRoute,
	[namedRoutes.SPACE_SETTINGS_SHAREABLE_LINKS.name]: ShareableLinkSpaceSettingsRoute,
	[namedRoutes.SPACE_SETTINGS_SHAREABLE_LINKS_CONFIGURATION.name]:
		ShareableLinkSpaceSettingsConfigurationRoute,
	[namedRoutes.ROLE_BASED_ACCESS_CONTROL_HUB.name]: RoleBasedAccessControlHubRoute,
	[namedRoutes.SPACE_LOOK_AND_FEEL_SETTINGS.name]: SpaceLookAndFeelSettingsRoute,
	[namedRoutes.ADMIN_ANNOUNCEMENT_BANNER_RENDERER.name]: AdminAnnouncementBannerRendererRoute,
	[namedRoutes.SPACE_MANAGER.name]: SpaceManagerRoute,
	[namedRoutes.USER_ACCESS.name]: UserAccessRoute,
	[namedRoutes.ADMIN_CENTER.name]: AdminCenterRoute,
	[namedRoutes.ADMIN_CENTER_SPACE.name]: AdminCenterSpaceRoute,
	[namedRoutes.ADMIN_SETTINGS.name]: AdminSettingsRoute,
	[namedRoutes.ADMIN_BULK_PERMISSIONS.name]: BulkSpacePermissionsRoute,
	[namedRoutes.ADMIN_SITE_SPACE_PERMISSIONS.name]: AdminSiteSpacePermissionsRoute,
	[namedRoutes.ADMIN_TEAM_CALENDARS.name]: AdminTeamCalendarsRoute,
	[namedRoutes.ADMIN_TEAM_CONTROLS.name]: AdminTeamControlsRoute,
	[namedRoutes.ADMIN_CONFIGURATION.name]: AdminConfigurationRoute,
	[namedRoutes.ADMIN_DEFAULT_SPACE_LOGO.name]: AdminDefaultSpaceLogoConfigRoute,
	[namedRoutes.ADMIN_HEADER_AND_FOOTER.name]: AdminHeaderAndFooterRoute,
	[namedRoutes.ADMIN_TEMPLATES_AND_BLUEPRINTS.name]: AdminTemplatesAndBlueprintsRoute,
	[namedRoutes.ADMIN_CODE_MACRO_CONFIGURATION.name]: AdminCodeMacroConfigurationRoute,
	[namedRoutes.ADMIN_COLOR_SCHEME.name]: AdminColorSchemeRoute,
	[namedRoutes.ADMIN_SECURITY_CONFIGURATION.name]: AdminSecurityConfigurationRoute,
	[namedRoutes.ADMIN_SHAREABLE_LINKS.name]: AdminShareableLinksRoute,
	[namedRoutes.ADMIN_SHAREABLE_LINKS_CONFIGURATION.name]: AdminShareableLinksConfigurationRoute,
	[namedRoutes.ADMIN_SPACE_REPORTS.name]: AdminSpaceReportsRoute,
	[namedRoutes.ADMIN_APPLICATION_NAVIGATOR.name]: AdminApplicationNavigatorRoute,
	[namedRoutes.ADMIN_TRASHED_SPACES.name]: AdminTrashedSpacesRoute,
	[namedRoutes.ADMIN_IMPORT_SPACES.name]: AdminImportSpacesRoute,
	[namedRoutes.ADMIN_JIRA_MACRO_REPAIR.name]: AdminJiraMacroRepairRoute,
	[namedRoutes.ADMIN_MACRO_USAGE.name]: AdminMacroUsageRoute,
	[namedRoutes.ADMIN_THEMES.name]: AdminThemesRoute,
	[namedRoutes.ADMIN_PDF_EXPORT.name]: AdminPdfExportRoute,
	[namedRoutes.ADMIN_WATERMARK_CONFIGURATION.name]: AdminWatermarkConfigurationRoute,
	[namedRoutes.ADMIN_DC_SCRIPT_MIGRATION.name]: AdminDCScriptMigrationRoute,
	[namedRoutes.ADMIN_NBM_MIGRATION.name]: AdminNBMMigrationRoute,
	[namedRoutes.ADMIN_NBM_MIGRATION_SCANS.name]: AdminNBMMigrationRoute,
	[namedRoutes.ADMIN_NBM_MIGRATION_SCAN.name]: AdminNBMMigrationRoute,
	[namedRoutes.ADMIN_NBM_MIGRATION_UNVERIFIED.name]: AdminNBMMigrationRoute,
	[namedRoutes.ADMIN_NBM_MIGRATION_UNSUPPORTED.name]: AdminNBMMigrationRoute,
	[namedRoutes.ADMIN_NBM_MIGRATION_RUN_TRANSFORMATIONS.name]: AdminNBMMigrationRoute,
	[namedRoutes.ADMIN_NBM_MIGRATION_TRANSFORMATIONS.name]: AdminNBMMigrationRoute,
	[namedRoutes.ADMIN_NBM_PERFORMANCE_REPORT.name]: AdminNBMPerformanceReportRoute,
	[namedRoutes.ADMIN_NBM_PERFORMANCE_REPORT_SCAN.name]: AdminNBMPerformanceReportRoute,
	[namedRoutes.SPACE_MEMBERS.name]: SpaceMembersRoute,
	[namedRoutes.TOPICS.name]: TopicsRoute,
	[namedRoutes.IMPORTER_STATUS_DASHBOARD.name]: ImporterDashboardRoute,
	[namedRoutes.ATTACHMENTS_PAGE.name]: AttachmentsPageRoute,
	[namedRoutes.AI_SEARCH_PAGE.name]: AISearchRoute,
	[namedRoutes.PERMISSIONS_EXPORT.name]: PermissionsExportRoute,
	[namedRoutes.PERMISSIONS_COMBINATIONS.name]: PermissionsCombinationsRoute,
	[namedRoutes.ADMIN_SYSTEM_OPERATIONS.name]: SystemOperationsRoute,
	[namedRoutes.NEW_AUDIT_LOG.name]: AuditLogRoute,
});
